import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Download, RefreshCw, ChevronDown, ChevronRight, Check, Archive, RotateCcw, Users, Trash2 } from 'lucide-react';
import { adminAPI, downloadBlob } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

function kr(v) {
    return Number(v || 0).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function currentPeriod() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function AdminFirmsPage() {
    const [period, setPeriod] = useState(currentPeriod());
    const [billing, setBilling] = useState(null);
    const [firms, setFirms] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(null);     // firm_id
    const [detail, setDetail] = useState(null);          // getFirm payload
    const [priceEdit, setPriceEdit] = useState({});      // firm_id -> string

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [b, f] = await Promise.all([adminAPI.firmBilling(period), adminAPI.listFirms()]);
            setBilling(b.data);
            setFirms(f.data.firms || []);
            const pe = {};
            (f.data.firms || []).forEach((x) => { pe[x.id] = x.price_per_client == null ? '' : String(x.price_per_client); });
            setPriceEdit(pe);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not load firms');
        } finally { setLoading(false); }
    }, [period]);

    useEffect(() => { load(); }, [load]);

    const billingById = {};
    (billing?.firms || []).forEach((r) => { billingById[r.firm_id] = r; });

    const [firmForm, setFirmForm] = useState(null);   // editable firm details
    const [savingFirm, setSavingFirm] = useState(false);
    const toggle = async (firmId) => {
        if (expanded === firmId) { setExpanded(null); setDetail(null); setFirmForm(null); return; }
        setExpanded(firmId); setDetail(null); setFirmForm(null);
        try {
            const r = await adminAPI.getFirm(firmId);
            setDetail(r.data);
            const f = r.data.firm || {};
            const c = f.contact || {}, b = f.billing_details || {};
            setFirmForm({ name: f.name || '', cvr_number: f.cvr_number || '',
                email: c.email || '', phone: c.phone || '', address: c.address || '',
                invoice_email: b.invoice_email || '' });
        } catch (e) { toast.error('Could not load firm detail'); }
    };
    const saveFirm = async (firmId) => {
        if (!firmForm) return;
        setSavingFirm(true);
        try {
            await adminAPI.updateFirm(firmId, {
                name: firmForm.name, cvr_number: firmForm.cvr_number,
                contact: { email: firmForm.email, phone: firmForm.phone, address: firmForm.address },
                billing_details: { invoice_email: firmForm.invoice_email },
            });
            toast.success('Firm details saved');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
        finally { setSavingFirm(false); }
    };

    const savePrice = async (firmId) => {
        const raw = (priceEdit[firmId] ?? '').trim();
        const val = raw === '' ? null : Number(raw);
        if (val != null && (Number.isNaN(val) || val < 0)) { toast.error('Price must be a number ≥ 0 (or blank to clear)'); return; }
        try {
            await adminAPI.setFirmPrice(firmId, val);
            toast.success(val == null ? 'Price cleared' : `Price set to ${kr(val)} DKK/client`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Could not set price'); }
    };

    const setClientStatus = async (tenantId, status) => {
        try {
            await adminAPI.setClientStatus(tenantId, status);
            toast.success(`Client ${status}`);
            load();
            if (expanded) { const r = await adminAPI.getFirm(expanded); setDetail(r.data); }
        } catch (e) { toast.error(e.response?.data?.detail || 'Could not update client'); }
    };

    const downloadCsv = async () => {
        try {
            const r = await adminAPI.downloadFirmBillingCsv(period);
            downloadBlob(r, `firm_billing_${period}.csv`);
        } catch (e) { toast.error('CSV export failed'); }
    };

    // ---- Volume-test harness (Part B rehearsal — test data only) ----
    const [volCount, setVolCount] = useState(200);
    const [volTenants, setVolTenants] = useState(2);
    const [vol, setVol] = useState(null);
    const [volBusy, setVolBusy] = useState(false);
    const refreshVol = useCallback(async () => {
        try { const r = await adminAPI.volumeTestStatus(); setVol(r.data); } catch (e) { /* ignore */ }
    }, []);
    useEffect(() => {
        if (!vol || vol.all_terminal || vol.total === 0) return;
        const t = setTimeout(refreshVol, 3000);  // poll while a batch is in flight
        return () => clearTimeout(t);
    }, [vol, refreshVol]);
    const runVolume = async () => {
        setVolBusy(true);
        try {
            const r = await adminAPI.volumeTestEnqueue(volCount, volTenants);
            toast.success(`Enqueued ${r.data.enqueued} test docs`);
            refreshVol();
        } catch (e) { toast.error(e.response?.data?.detail || 'Enqueue failed'); }
        finally { setVolBusy(false); }
    };
    const cleanupVolume = async () => {
        setVolBusy(true);
        try {
            const r = await adminAPI.volumeTestCleanup();
            toast.success(`Cleaned up ${r.data.deleted_docs} docs / ${r.data.deleted_jobs} jobs`);
            setVol(null);
        } catch (e) { toast.error('Cleanup failed'); }
        finally { setVolBusy(false); }
    };

    // ---- GDPR erasure (destructive, irreversible; name-echo confirm) ----
    const [erase, setErase] = useState(null);   // { scope:'tenant'|'firm', id, name }
    const [eraseInput, setEraseInput] = useState('');
    const [erasing, setErasing] = useState(false);
    const openErase = (scope, id, name) => { setErase({ scope, id, name }); setEraseInput(''); };
    const doErase = async () => {
        if (!erase || eraseInput.trim() !== (erase.name || '').trim()) { toast.error('Type the exact name to confirm'); return; }
        setErasing(true);
        try {
            const r = erase.scope === 'firm'
                ? await adminAPI.gdprEraseFirm(erase.id, eraseInput.trim())
                : await adminAPI.gdprEraseTenant(erase.id, eraseInput.trim());
            const d = r.data;
            toast.success(`Erased ${d.total_rows} rows across ${Object.keys(d.collections_purged).length} collections`);
            setErase(null); setExpanded(null); setDetail(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erasure failed'); }
        finally { setErasing(false); }
    };

    const t = billing?.totals;

    return (
        <div className="space-y-8" data-testid="admin-firms-page">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-heading text-3xl font-bold flex items-center gap-2">
                        <Building2 className="h-7 w-7 text-primary" /> Firms &amp; billing
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Platform-admin oversight of every firm and its clients. Per-client billing is calculated only — no payments are taken here.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-sm text-muted-foreground">Period</label>
                    <input type="month" value={period} onChange={(e) => setPeriod(e.target.value || currentPeriod())}
                           className="h-9 rounded-md border px-2 text-sm" data-testid="billing-period" />
                    <Button variant="ghost" size="icon" onClick={load} title="Refresh"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
                    <Button onClick={downloadCsv} data-testid="billing-csv"><Download className="h-4 w-4 mr-1" /> Download CSV</Button>
                </div>
            </div>

            {/* Period totals */}
            {t && (
                <div className="flex flex-wrap gap-3 text-sm">
                    <Badge variant="outline">Firms {t.firms}</Badge>
                    <Badge variant="outline">Active clients {t.active_clients}</Badge>
                    <Badge variant="secondary">Ex-VAT {kr(t.ex_vat)} DKK</Badge>
                    <Badge variant="secondary">VAT 25% {kr(t.vat_amount)} DKK</Badge>
                    <Badge variant="success">Incl-VAT {kr(t.incl_vat)} DKK</Badge>
                    <span className="text-muted-foreground self-center">period {billing.period}</span>
                </div>
            )}

            <Card>
                <CardHeader><CardTitle className="text-lg">Firms ({firms.length})</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                    {firms.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4 text-center">No firms.</p>
                    ) : firms.map((f) => {
                        const b = billingById[f.id];
                        const isOpen = expanded === f.id;
                        return (
                            <div key={f.id} className="rounded-md border" data-testid={`firm-${f.id}`}>
                                <div className="flex items-center gap-3 p-3">
                                    <button onClick={() => toggle(f.id)} className="text-muted-foreground shrink-0">
                                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                    </button>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate flex items-center gap-2">
                                            {f.name}
                                            {f.status !== 'active' && <Badge variant="outline" className="border-amber-400 text-amber-700">{f.status}</Badge>}
                                            {f.name === 'Unassigned Clients' && <Badge variant="outline" className="border-red-400 text-red-700">reassign</Badge>}
                                        </div>
                                        <div className="text-xs text-muted-foreground font-mono">
                                            CVR {f.cvr_number || '—'} · {f.sub_user_count} user(s) · {f.active_client_count} active / {f.total_client_count} clients
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <div className="flex items-center gap-1">
                                            <span className="text-xs text-muted-foreground">DKK/client</span>
                                            <Input className="h-8 w-24 font-mono text-right" value={priceEdit[f.id] ?? ''}
                                                   placeholder="not set"
                                                   onChange={(e) => setPriceEdit((p) => ({ ...p, [f.id]: e.target.value }))}
                                                   data-testid={`price-${f.id}`} />
                                            <Button size="sm" variant="outline" className="h-8" onClick={() => savePrice(f.id)} data-testid={`save-price-${f.id}`}>Save</Button>
                                        </div>
                                        <div className="text-right w-40">
                                            {b && b.price_set ? (
                                                <>
                                                    <div className="text-sm font-semibold">{kr(b.incl_vat)} DKK <span className="text-xs font-normal text-muted-foreground">incl</span></div>
                                                    <div className="text-xs text-muted-foreground">{kr(b.ex_vat)} ex + {kr(b.vat_amount)} VAT</div>
                                                </>
                                            ) : <div className="text-xs text-amber-700">price not set</div>}
                                        </div>
                                    </div>
                                </div>

                                {isOpen && (
                                    <div className="border-t bg-muted/20 p-3 space-y-4" data-testid={`firm-detail-${f.id}`}>
                                        {/* Editable firm details */}
                                        {firmForm && (
                                            <div>
                                                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Firm details</div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                    {[
                                                        ['name', 'Name'], ['cvr_number', 'CVR'], ['email', 'Contact email'],
                                                        ['phone', 'Phone'], ['address', 'Address'], ['invoice_email', 'Billing/invoice email'],
                                                    ].map(([k, label]) => (
                                                        <div key={k} className="flex items-center gap-2">
                                                            <span className="text-xs text-muted-foreground w-32 shrink-0">{label}</span>
                                                            <Input className="h-8" value={firmForm[k]} placeholder="not set"
                                                                   onChange={(e) => setFirmForm((p) => ({ ...p, [k]: e.target.value }))}
                                                                   data-testid={`firm-${k}-${f.id}`} />
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="mt-2">
                                                    <Button size="sm" onClick={() => saveFirm(f.id)} disabled={savingFirm} data-testid={`save-firm-${f.id}`}>
                                                        {savingFirm ? 'Saving…' : 'Save firm details'}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}

                                        {/* AI usage this month (visibility only) */}
                                        {detail?.usage_this_month && (
                                            <div className="text-xs text-muted-foreground">
                                                <span className="uppercase tracking-wide">AI usage ({detail.usage_this_month.period}): </span>
                                                <span className="font-mono text-foreground">{Number(detail.usage_this_month.total_tokens).toLocaleString('da-DK')} tokens</span>
                                                {' · '}<span className="font-mono text-foreground">${Number(detail.usage_this_month.cost_usd).toFixed(2)}</span> est.
                                                {' · '}{detail.usage_this_month.extraction_count} extractions
                                            </div>
                                        )}

                                        {/* Billable client itemization */}
                                        <div>
                                            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                                                Billable clients this period ({b?.active_client_count || 0}) — {b?.price_set ? `${kr(b.price_per_client)} DKK each` : 'price not set'}
                                            </div>
                                            {(b?.billable_clients || []).length === 0 ? (
                                                <p className="text-xs text-muted-foreground">No active clients counted.</p>
                                            ) : (
                                                <div className="space-y-1">
                                                    {b.billable_clients.map((c) => (
                                                        <div key={c.id} className="text-sm flex items-center gap-2">
                                                            <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                                                            <span className="truncate">{c.name}</span>
                                                            <Badge variant="outline" className="font-mono">CVR {c.cvr_number || '—'}</Badge>
                                                            <Badge variant="outline" className="ml-auto">counted</Badge>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Clients — full identity + read-only system health (oversight only) */}
                                        <div>
                                            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Clients — full identity &amp; health</div>
                                            {!detail ? <p className="text-xs text-muted-foreground">Loading…</p> :
                                                detail.clients.length === 0 ? <p className="text-xs text-muted-foreground">No clients.</p> :
                                                    <div className="space-y-2">
                                                        {detail.clients.map((c) => {
                                                            const h = c.health || {};
                                                            const ns = (v) => (v === null || v === undefined || v === '' ? <span className="text-muted-foreground/60">not set</span> : v);
                                                            const stuck = (h.queue_failed || 0) > 0;
                                                            return (
                                                                <div key={c.id} className="rounded border bg-background p-2" data-testid={`client-${c.id}`}>
                                                                    <div className="flex items-center gap-2 flex-wrap">
                                                                        <span className="font-medium">{c.name}</span>
                                                                        <Badge variant="outline" className="font-mono">CVR {c.cvr_number || '—'}</Badge>
                                                                        <Badge variant="outline">{c.delivery_target}</Badge>
                                                                        <Badge variant={c.status === 'active' ? 'success' : 'outline'}>{c.status}</Badge>
                                                                        {stuck && <Badge variant="outline" className="border-red-400 text-red-700">{h.queue_failed} failed</Badge>}
                                                                        {c.status === 'active' ? (
                                                                            <Button size="sm" variant="ghost" className="h-7 ml-auto" onClick={() => setClientStatus(c.id, 'archived')}><Archive className="h-3 w-3 mr-1" /> Archive</Button>
                                                                        ) : (
                                                                            <Button size="sm" variant="ghost" className="h-7 ml-auto" onClick={() => setClientStatus(c.id, 'active')}><RotateCcw className="h-3 w-3 mr-1" /> Activate</Button>
                                                                        )}
                                                                        <Button size="sm" variant="ghost" className="h-7 text-red-600 hover:text-red-700" onClick={() => openErase('tenant', c.id, c.name)} title="Permanently erase this client and all its data (GDPR)" data-testid={`erase-${c.id}`}><Trash2 className="h-3 w-3 mr-1" /> Erase</Button>
                                                                    </div>
                                                                    <div className="mt-1 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-0.5 text-xs">
                                                                        <div>Address: {ns(c.address)}</div>
                                                                        <div>VAT profile: {ns(c.vat_profile)}</div>
                                                                        <div>e-conomic agreement: {ns(c.economic_agreement_number)}</div>
                                                                        <div>e-conomic journal: {ns(c.economic_journal_number)}</div>
                                                                        <div>e-conomic grant: {c.economic_grant_set ? 'set' : <span className="text-muted-foreground/60">not set</span>}</div>
                                                                        <div>Documents: {h.document_count ?? 0}</div>
                                                                        <div>Last activity: {h.last_activity ? new Date(h.last_activity).toLocaleString('da-DK') : <span className="text-muted-foreground/60">—</span>}</div>
                                                                        <div className={stuck ? 'text-red-700' : ''}>Queue: {h.queue_done ?? 0} done · {h.queue_pending ?? 0} pending · {h.queue_processing ?? 0} processing · {h.queue_failed ?? 0} failed</div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>}
                                        </div>

                                        {/* Sub-users */}
                                        <div>
                                            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1"><Users className="h-3 w-3" /> Sub-users</div>
                                            {!detail ? <p className="text-xs text-muted-foreground">Loading…</p> :
                                                detail.sub_users.length === 0 ? <p className="text-xs text-muted-foreground">No firm users.</p> :
                                                    detail.sub_users.map((u) => (
                                                        <div key={u.id} className="text-sm flex items-center gap-2 py-0.5">
                                                            <span className="truncate">{u.name || u.email}</span>
                                                            <span className="text-xs text-muted-foreground">{u.email}</span>
                                                            <Badge variant="outline" className="ml-auto">{u.firm_role || u.role}</Badge>
                                                            {u.staff_access && <Badge variant="secondary">{u.staff_access}</Badge>}
                                                        </div>
                                                    ))}
                                        </div>

                                        {/* Danger zone — GDPR firm erasure */}
                                        <div className="rounded border border-red-300 bg-red-50 p-2">
                                            <div className="text-xs uppercase tracking-wide text-red-700 mb-1">Danger zone — GDPR erasure</div>
                                            <p className="text-xs text-red-700 mb-2">Permanently delete this firm, <strong>all its clients and their data</strong>, and its user logins. Irreversible.</p>
                                            <Button size="sm" variant="outline" className="h-7 border-red-400 text-red-700 hover:bg-red-100" onClick={() => openErase('firm', f.id, f.name)} data-testid={`erase-firm-${f.id}`}>
                                                <Trash2 className="h-3 w-3 mr-1" /> Erase entire firm (GDPR)
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </CardContent>
            </Card>

            {/* GDPR erasure confirm modal — requires typing the exact name */}
            {erase && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="erase-modal">
                    <Card className="w-full max-w-md border-red-300">
                        <CardHeader>
                            <CardTitle className="text-lg text-red-700 flex items-center gap-2"><Trash2 className="h-5 w-5" /> Permanently erase {erase.scope}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <p className="text-sm">
                                This <strong>irreversibly deletes</strong> {erase.scope === 'firm'
                                    ? <>the firm <strong>{erase.name}</strong>, every one of its clients and all their documents/bank lines/vouchers, and its user logins.</>
                                    : <>the client <strong>{erase.name}</strong> and all its documents (including the original files), bank lines, vouchers, and history.</>}
                                {' '}There is no undo.
                            </p>
                            <div className="space-y-1">
                                <label className="text-xs text-muted-foreground">Type the {erase.scope} name to confirm: <span className="font-mono">{erase.name}</span></label>
                                <Input value={eraseInput} onChange={(e) => setEraseInput(e.target.value)} placeholder={erase.name} data-testid="erase-confirm-input" autoFocus />
                            </div>
                            <div className="flex items-center justify-end gap-2">
                                <Button variant="ghost" onClick={() => setErase(null)} disabled={erasing}>Cancel</Button>
                                <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={doErase}
                                        disabled={erasing || eraseInput.trim() !== (erase.name || '').trim()} data-testid="erase-confirm-btn">
                                    {erasing ? 'Erasing…' : 'Erase permanently'}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Volume-test harness (Part B rehearsal — test data only) */}
            <Card>
                <CardHeader><CardTitle className="text-lg">Volume test (Part B queue) — test data only</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                        Rehearse pilot scale: duplicate your existing invoice corpus into dedicated <strong>test tenants</strong> (never real client data) and run them through the durable queue. Clean up when done.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                        <label className="text-sm text-muted-foreground">Docs</label>
                        <Input type="number" className="h-8 w-24" value={volCount} min={1} max={500}
                               onChange={(e) => setVolCount(e.target.value)} data-testid="vol-count" />
                        <label className="text-sm text-muted-foreground">Tenants</label>
                        <Input type="number" className="h-8 w-16" value={volTenants} min={1} max={2}
                               onChange={(e) => setVolTenants(Number(e.target.value))} />
                        <Button onClick={runVolume} disabled={volBusy} data-testid="vol-run">Enqueue batch</Button>
                        <Button variant="ghost" onClick={refreshVol} disabled={volBusy}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
                        <Button variant="outline" onClick={cleanupVolume} disabled={volBusy} data-testid="vol-cleanup">Clean up</Button>
                    </div>
                    {vol && vol.total > 0 && (
                        <div className="text-sm grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1" data-testid="vol-metrics">
                            <div><strong>{vol.done}</strong> / {vol.total} done {vol.all_terminal ? '✓' : `· ${vol.pending} pending`}</div>
                            <div>Failed: <span className={vol.failed ? 'text-red-600' : ''}>{vol.failed}</span></div>
                            <div>Max concurrency: <strong>{vol.max_concurrency_observed}</strong></div>
                            <div>Throughput: {vol.throughput_docs_per_min ?? '—'} docs/min</div>
                            <div>Token peak (60s): {vol.token_peak_rolling_60s} / {vol.token_budget}</div>
                            <div>Recovered/retried: {vol.retried_or_recovered}</div>
                            <div className="col-span-2 md:col-span-3 text-xs text-muted-foreground">
                                Fairness: {Object.entries(vol.per_tenant_fairness || {}).map(([t, v]) => `${v.done}/${v.total} (ranks ${v.first_done_rank ?? '-'}–${v.last_done_rank ?? '-'})`).join('  ·  ')}
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            <p className="text-xs text-muted-foreground">
                Billing is a calculation only — no payments, cards, or Stripe. Use the CSV to raise invoices manually. Amount owed = active clients × price per client; Danish VAT (25%) is shown as its own line.
            </p>
        </div>
    );
}
